import { AxiosError } from 'axios'
import { DataTableFilterMetaData } from 'primereact/datatable'
import { useQuery } from '@tanstack/react-query'
import { toMilliseconds } from '../Functions/toMilliseconds'
import useWbapiInstance, { LazyState } from './useWbapiInstance'

export interface Member {
  id: string
  aaneelId: number
  memberId: string
  mbi: string
  lastName: string
  firstName: string
  fullName: string
  dtOfBirth: Date
  marketId: number
  marketName: string
  neighborhoodId: number
  neighborhoodName: string
  community: string
  termDt?: Date
  eligible: boolean
  engaged: boolean
  engagedDt?: Date
  deceased: boolean
  deceasedDt?: Date
  gender: string
  dncPhone: boolean
  dncEmail: boolean
  dncText: boolean
  primaryPhone?: string
  payerName: string
  planName?: string
  termReason?: string
  pendingTransition: boolean
  isPendingDNC: boolean
  stratification: string
  psvEligible: boolean
  f2FEligible: boolean
  dropinIneligible: boolean
  telehealthWilling: boolean
  videoAble: boolean
  restingUntil?: Date
  primaryEmail?: string
  providerId?: number
  providerName?: string
  providerEmployeeId?: string
  // Metrics
  disengaged?: boolean
  erVisitsLastThreeMonths?: number
  erVisitsLastTwelveMonths?: number
  acuteAdmitsLastThreeMonths?: number
  currentNumberOfMedications?: number
  lastAdmitDischargeDate?: Date
  firstOutreachAttemptedDate?: Date
  firstOutreachContactedDate?: Date
  lastOutreachAttemptedDate?: Date
  lastOutreachContactedDate?: Date
  pcpNameFromQuality: string
  recentBhIndicator?: boolean
}

export interface PaginationMetadata {
  currentPage: number
  pageSize: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
  previousPage: number | null
  nextPage: number | null
}

export interface PagedResult<T> {
  items: T[]
  metadata: PaginationMetadata
}

export const useMembersOdata = (state: LazyState) => {
  const { api } = useWbapiInstance()
  const queryKey = ['members', state]

  const get = async () => {
    const params = generateSieveQuery(state)
    const { data } = await api.get(`/api/v1/members`, {
      params,
    })
    return data
  }

  return useQuery<PagedResult<Member>, AxiosError>({
    queryKey,
    queryFn: get,
    staleTime: toMilliseconds({ min: 5 }),
    enabled: (state.filters.payerId as DataTableFilterMetaData)?.value?.length !== 0,
  })
}

interface SieveQuery {
  Filters?: string
  Sorts?: string
  Page?: number
  PageSize?: number
}

const matchModeToSieveOperator: Record<string, string> = {
  contains: '@=',
  startsWith: '_=',
  endsWith: '_-=',
  notContains: '!@=',
  equals: '==',
  notEquals: '!=',
  in: '==',
  notIn: '!=',
  lt: '<',
  lte: '<=',
  gt: '>',
  gte: '>=',
  between: '>=',
  dateIs: '==',
  dateIsNot: '!=',
  dateBefore: '<',
  dateAfter: '>',
}

function formatValue(value: any): string {
  if (value instanceof Date) {
    return value.toISOString().split('T')[0]
  }
  return String(value.replace(/,/g, '\\,'))
}

const generateSieveQuery = (state: LazyState): SieveQuery => {
  const sieveFilters: string[] = []
  const { first, rows, sortField, sortOrder, filters } = state

  for (const column in filters) {
    const filterInfo = filters[column] as DataTableFilterMetaData
    const value = filterInfo.value
    const matchMode = filterInfo.matchMode

    if (matchMode == undefined || value === undefined) continue

    const sieveOperator = matchModeToSieveOperator[matchMode]
    if (sieveOperator == undefined) {
      throw new Error(`Unsupported match mode: ${matchMode}`)
    }

    if (column == 'payerPortalStatus') {
      if (filterInfo.value == 'Termed') sieveFilters.push(`TermDt!=null`)
      else {
        sieveFilters.push(`TermDt==null`)
        if (filterInfo.value == 'Engaged') sieveFilters.push(`Engaged==true`)
        else sieveFilters.push(`Engaged==false`)
      }
      continue
    }

    if (matchMode === 'in' || matchMode === 'notIn') {
      if (!Array.isArray(value) || value.length === 0) continue
      const values = value.map((v: any) => formatValue(v)).join('|')
      sieveFilters.push(`${column}${sieveOperator}${values}`)
    } else if (matchMode === 'between') {
      if (!Array.isArray(value) || (value[0] === null && value[1] === null)) continue
      if (value[0] !== null) {
        sieveFilters.push(`${column}>=${formatValue(value[0])}`)
      }
      if (value[1] !== null) {
        sieveFilters.push(`${column}<=${formatValue(value[1])}`)
      }
    } else {
      if (
        value == null &&
        (matchMode === 'contains' || matchMode === 'startsWith' || matchMode === 'endsWith' || matchMode === 'notContains')
      ) {
        continue
      }
      sieveFilters.push(`${column}${sieveOperator}${formatValue(value)}`)
    }
  }

  const query: SieveQuery = {}

  if (sieveFilters.length > 0) {
    query.Filters = sieveFilters.join(',')
  }

  if (sortField) {
    if (sortField == 'aaneelIdStr') {
      query.Sorts = sortOrder === 1 ? 'aaneelId' : `-aaneelId`
    } else query.Sorts = sortOrder === 1 ? sortField : `-${sortField}`
  }

  if (rows) {
    query.PageSize = rows
    query.Page = Math.floor((first ?? 0) / rows) + 1
  }

  return query
}
